import React, { useContext, useEffect, useState } from 'react';
import Modal from '@atlaskit/modal-dialog';
import styled from 'styled-components';

import Badge from 'kingpin/atoms/Badge';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import ToastContext from 'contexts/ToastContext';
import { importAgentKb } from 'services/agentKb';

import { IMPORT_OUTCOME_BADGES, IMPORT_OUTCOME_LABELS } from './constants';

// Importing a version folder is a MERGE, not an overwrite: the graphql team
// keeps authoring the KB outside this app, so every import is a three-way diff
// against what we last imported. Conflicts are shown and left alone unless the
// reviewer explicitly takes upstream's side — last-upload-wins is how
// hand-written guidance disappears.

const ContentDiv = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 60vh;
  overflow-y: auto;
`;

const EntryRow = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
  cursor: pointer;
`;

const ImportDialog = ({
  version,
  close,
}: {
  // Which bucket folder to read. Omitted falls back to the newest, which is
  // the endpoint's own default.
  version?: string;
  close: () => void;
}) => {
  const { api } = useContext(CloudFunctionClientContext);
  const { showToast } = useContext(ToastContext);
  const [plan, setPlan] = useState<AgentKb.ImportPlan | null>(null);
  const [takeTheirs, setTakeTheirs] = useState<string[]>([]);
  // Retired-upstream artefacts whose local draft has moved on. Untouched ones
  // are archived without asking; these need a tick, because the draft is work
  // nobody has published.
  const [archiveEdited, setArchiveEdited] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isApplying, setIsApplying] = useState<boolean>(false);

  useEffect(() => {
    let isActive = true;
    importAgentKb(api, { version, apply: false }).then((result) => {
      if (!isActive) {
        return;
      }
      if (result.ok) {
        setPlan(result.data.plan);
      } else {
        showToast(result.error);
      }
      setIsLoading(false);
    });
    return () => {
      isActive = false;
    };
  }, [api, showToast, version]);

  const onApply = async () => {
    setIsApplying(true);
    const result = await importAgentKb(api, {
      version,
      apply: true,
      takeTheirs,
      archiveEdited,
    });
    setIsApplying(false);
    if (result.ok) {
      const { applied = [], archived = [] } = result.data;
      showToast(
        `Imported ${applied.length} artefact(s)` +
          (archived.length > 0 ? `, archived ${archived.length}` : ''),
      );
      close();
    } else {
      showToast(result.error);
    }
  };

  const conflicts = (plan?.entries ?? []).filter(
    (entry) => entry.outcome === 'conflict',
  );
  const retiredCleanly = (plan?.entries ?? []).filter(
    (entry) => entry.outcome === 'removedUpstream' && !entry.hasLocalEdits,
  );
  const changes = (plan?.entries ?? []).filter(
    (entry) => entry.outcome !== 'unchanged',
  );

  return (
    <Modal width="x-large" onClose={close} shouldScrollInViewport>
      <ContentDiv data-testid="agent-kb-import-dialog">
        <Typography.Header type="H4">
          Import from the knowledge base bucket
        </Typography.Header>
        <Typography.Body type="Body 12" color="var(--color-text-secondary)">
          {version ?? 'the newest version folder'}
        </Typography.Body>
        {isLoading && (
          <Typography.Body type="Body 13">Comparing versions…</Typography.Body>
        )}
        {plan && (
          <>
            <Typography.Body type="Body 13" color="var(--color-text-secondary)">
              Version {plan.version} · {changes.length} change(s),{' '}
              {conflicts.length} conflict(s). Unchanged artefacts are not
              listed.
            </Typography.Body>
            {retiredCleanly.length > 0 && (
              <Typography.Body
                type="Body 13"
                color="var(--color-text-secondary)"
              >
                {retiredCleanly.length} artefact(s) are not in this version and
                will be archived — they still match the bytes last imported, so
                nothing of yours is lost. A publish would otherwise put them
                back.
              </Typography.Body>
            )}
            {changes.length === 0 && (
              <Typography.Body type="Body 13">
                This version matches the draft exactly. Nothing to import.
              </Typography.Body>
            )}
            {changes.map((entry) => (
              <EntryRow key={entry.artefactId}>
                {entry.outcome === 'conflict' && (
                  <input
                    type="checkbox"
                    checked={takeTheirs.includes(entry.artefactId)}
                    onChange={(event) =>
                      setTakeTheirs(
                        event.target.checked
                          ? [...takeTheirs, entry.artefactId]
                          : takeTheirs.filter((id) => id !== entry.artefactId),
                      )
                    }
                    data-testid={`agent-kb-import-take-${entry.artefactId}`}
                  />
                )}
                {entry.outcome === 'removedUpstream' && entry.hasLocalEdits && (
                  <input
                    type="checkbox"
                    checked={archiveEdited.includes(entry.artefactId)}
                    onChange={(event) =>
                      setArchiveEdited(
                        event.target.checked
                          ? [...archiveEdited, entry.artefactId]
                          : archiveEdited.filter(
                              (id) => id !== entry.artefactId,
                            ),
                      )
                    }
                    data-testid={`agent-kb-import-archive-${entry.artefactId}`}
                  />
                )}
                <Badge
                  badgeType={
                    IMPORT_OUTCOME_BADGES[
                      entry.outcome
                    ] as React.ComponentProps<typeof Badge>['badgeType']
                  }
                  text={IMPORT_OUTCOME_LABELS[entry.outcome]}
                />
                <Typography.Body type="Body 13">
                  {entry.artefactId}
                </Typography.Body>
                {entry.outcome === 'conflict' && (
                  <Typography.Body
                    type="Body 12"
                    color="var(--color-text-secondary)"
                  >
                    Both sides changed — tick to take the upstream version.
                  </Typography.Body>
                )}
              </EntryRow>
            ))}
          </>
        )}
        <Row centerAlign spaceBetween>
          <Button type="Ghost" size="Small" label="Cancel" onClick={close} />
          <Button
            type="Primary"
            size="Small"
            label={isApplying ? 'Importing…' : 'Import'}
            isDisabled={!plan || changes.length === 0 || isApplying}
            onClick={onApply}
          />
        </Row>
      </ContentDiv>
    </Modal>
  );
};

export default ImportDialog;
